import { SERIES_COLORS, TYPEFACES, type StromaTokens, type StromaTypeface } from './tokens';

export const CHART_CSS = {
  text: 'var(--ink)',
  textSecondary: 'var(--ink-2)',
  textMuted: 'var(--ink-3)',
  surface: 'var(--surface)',
  surface2: 'var(--surface2)',
  sunk: 'var(--sunk)',
  line: 'var(--line)',
  grid: 'var(--line-soft)',
  accent: 'var(--accent)',
  accent2: 'var(--accent2)',
  agent: 'var(--agent)',
  onAccent: 'var(--on-accent)',
  positive: 'var(--ok)',
  negative: 'var(--bad)',
  warning: 'var(--warn)',
  mono: 'var(--mono)',
  series: SERIES_COLORS,
} as const;

export const CHART_CONTRAST_DARK = '#000000' as const;
export const CHART_CONTRAST_LIGHT = '#ffffff' as const;

export function resolveChartColor(color: string, root: Element = document.documentElement): string {
  const match = color.match(/^var\((--[^)]+)\)$/);
  return match ? getComputedStyle(root).getPropertyValue(match[1]).trim() || color : color;
}

export function contrastTextColor(color: string): '#000000' | '#ffffff' {
  const match = color.match(/[\da-f]{2}/gi);
  if (!match || match.length < 3) return CHART_CONTRAST_LIGHT;
  const linear = match.slice(0, 3).map((hex) => {
    const channel = parseInt(hex, 16) / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
  const contrast = (foreground: number) => (Math.max(foreground, luminance) + 0.05) / (Math.min(foreground, luminance) + 0.05);
  return contrast(0) >= contrast(1) ? CHART_CONTRAST_DARK : CHART_CONTRAST_LIGHT;
}

export function categoricalTokenRange(_tokens: StromaTokens): string[] {
  return [...SERIES_COLORS];
}

let gradientSequence = 0;

export function setPlotBackground(plot: SVGSVGElement | HTMLElement, background: string): void {
  const svg = plot instanceof SVGSVGElement
    ? plot
    : Array.from(plot.children).reverse().find((child): child is SVGSVGElement => child instanceof SVGSVGElement);
  svg?.style.setProperty('--plot-background', background);
}

export function addSvgSeriesGradient(svg: SVGSVGElement, mark: 'line' | 'area'): void {
  const id = `stroma-series-gradient-${gradientSequence++}`;
  const ns = 'http://www.w3.org/2000/svg';
  const defs = document.createElementNS(ns, 'defs');
  const gradient = document.createElementNS(ns, 'linearGradient');
  gradient.id = id;
  gradient.setAttribute('gradientUnits', 'userSpaceOnUse');
  gradient.setAttribute('x1', '0');
  gradient.setAttribute('y1', '0');
  gradient.setAttribute('x2', svg.getAttribute('width') || '100%');
  gradient.setAttribute('y2', '0');
  const start = document.createElementNS(ns, 'stop');
  start.setAttribute('offset', '0%');
  start.setAttribute('stop-color', CHART_CSS.accent);
  const end = document.createElementNS(ns, 'stop');
  end.setAttribute('offset', '100%');
  end.setAttribute('stop-color', CHART_CSS.accent2);
  gradient.append(start, end);
  defs.append(gradient);
  svg.prepend(defs);
  svg.querySelector(`g[aria-label="${mark}"]`)?.setAttribute(mark === 'line' ? 'stroke' : 'fill', `url(#${id})`);
}

export function categoricalChartRange(type?: string): readonly string[] | undefined {
  return type === 'nominal' || type === 'ordinal' ? CHART_CSS.series : undefined;
}

type Aggregate = 'sum' | 'mean' | 'median' | 'count' | 'min' | 'max';

function aggregateValues(values: number[], aggregate?: Aggregate): number | undefined {
  if (!values.length) return undefined;
  if (!aggregate) return values[values.length - 1];
  if (aggregate === 'count') return values.length;
  if (aggregate === 'sum') return values.reduce((sum, value) => sum + value, 0);
  if (aggregate === 'mean') return values.reduce((sum, value) => sum + value, 0) / values.length;
  if (aggregate === 'min') return Math.min(...values);
  if (aggregate === 'max') return Math.max(...values);
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

export function plottedEndPoints(
  data: Record<string, unknown>[],
  { xField, yField, seriesField, aggregate, xType }: { xField?: string; yField?: string; seriesField?: string; aggregate?: Aggregate; xType?: string },
): Record<string, unknown>[] {
  if (!data.length || !xField || !yField) return [];
  const series = new Map<unknown, Map<unknown, Record<string, unknown>[]>>();
  data.forEach((row) => {
    const seriesKey = seriesField ? row[seriesField] : '__single__';
    const byX = series.get(seriesKey) || new Map<unknown, Record<string, unknown>[]>();
    const rows = byX.get(row[xField]) || [];
    rows.push(row);
    byX.set(row[xField], rows);
    series.set(seriesKey, byX);
  });
  return Array.from(series.values()).flatMap((byX) => {
    const comparable = (value: unknown): number | string => {
      if (xType === 'quantitative') {
        const number = Number(value);
        return Number.isFinite(number) ? number : Number.NEGATIVE_INFINITY;
      }
      if (xType === 'temporal') {
        const time = value instanceof Date ? value.getTime() : new Date(value as any).getTime();
        return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY;
      }
      return value instanceof Date ? value.getTime() : typeof value === 'number' ? value : String(value ?? '');
    };
    const latest = Array.from(byX.entries()).sort(([a], [b]) => {
      const left = comparable(a), right = comparable(b);
      return left < right ? -1 : left > right ? 1 : 0;
    }).pop();
    if (!latest) return [];
    const [, rows] = latest;
    const values = rows.map((row) => Number(row[yField])).filter(Number.isFinite);
    const value = aggregate === 'count' ? rows.length : aggregateValues(values, aggregate);
    return value === undefined ? [] : [{ ...rows[rows.length - 1], [yField]: value }];
  });
}

export function vegaLiteConfig(tokens: StromaTokens, typeface: StromaTypeface = 'brico') {
  const fonts = TYPEFACES[typeface];
  const seriesGradient = {
    gradient: 'linear', x1: 0, y1: 0, x2: 1, y2: 0,
    stops: [{ offset: 0, color: tokens.accent }, { offset: 1, color: tokens.accent2 }],
  };
  return {
    background: 'transparent',
    font: fonts.body,
    axis: {
      domainColor: tokens.line,
      gridColor: tokens['line-soft'],
      labelColor: tokens['ink-2'],
      labelFont: fonts.body,
      titleColor: tokens.ink,
      titleFont: fonts.body,
    },
    legend: { labelColor: tokens['ink-2'], labelFont: fonts.body, titleColor: tokens.ink, titleFont: fonts.body },
    title: { color: tokens.ink, font: fonts.display, fontWeight: 700 },
    range: { category: categoricalTokenRange(tokens) },
    line: { color: seriesGradient, strokeWidth: 2.5 },
    area: { color: seriesGradient, opacity: 0.2 },
    point: { color: tokens.accent, filled: true, size: 54, stroke: tokens.surface, strokeWidth: 2 },
    view: { stroke: 'transparent' },
  };
}

export function rechartsTheme(tokens: StromaTokens, typeface: StromaTypeface = 'brico') {
  const fonts = TYPEFACES[typeface];
  return {
    colors: categoricalTokenRange(tokens),
    cartesianGrid: { stroke: tokens['line-soft'] },
    axis: { stroke: tokens.line, tick: { fill: tokens['ink-2'], fontFamily: fonts.body, fontSize: 12 } },
    seriesGradient: { start: tokens.accent, end: tokens.accent2 },
    line: { stroke: tokens.accent, strokeWidth: 2.5, dot: false, endDot: { fill: tokens.accent2, stroke: tokens.surface, strokeWidth: 3, r: 5 } },
    area: { stroke: tokens.accent, fill: tokens['accent-tint'], endDot: { fill: tokens.accent2, stroke: tokens.surface, strokeWidth: 3, r: 5 } },
    tooltip: { backgroundColor: tokens.side1, borderColor: tokens['side-hover'], color: tokens['side-hi'] },
  };
}
